// Page-level building blocks: page header, cards, tables, badges, loaders, empty states.
import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import { cn } from "./cn";

export function PageHeader({ title, description, actions, breadcrumbs, className }) {
  return (
    <div className={cn("flex flex-wrap items-end justify-between gap-3 border-b border-line bg-panel px-6 pt-5 pb-4", className)}>
      <div className="min-w-0">
        {breadcrumbs && <div className="mb-1 text-[12px] text-faint">{breadcrumbs}</div>}
        <h1 className="m-0 text-[20px] leading-tight font-semibold tracking-tight text-fg">{title}</h1>
        {description && <p className="m-0 mt-1 text-[13px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageBody({ className, children }) {
  return <div className={cn("space-y-4 p-6", className)}>{children}</div>;
}

export function Card({ className, children, ...props }) {
  return (
    <div className={cn("rounded-xl border border-line bg-panel shadow-card", className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions, className }) {
  return (
    <div className={cn("flex items-start justify-between gap-3 border-b border-line px-4 py-3", className)}>
      <div className="min-w-0">
        <h3 className="m-0 text-[13.5px] font-semibold text-fg">{title}</h3>
        {description && <p className="m-0 mt-0.5 text-[12.5px] text-muted">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

/* ---------- Table ---------- */
export function Table({ className, children, maxHeight }) {
  return (
    <div className={cn("overflow-auto", className)} style={maxHeight ? { maxHeight } : undefined}>
      <table className="w-full border-collapse text-[13px]">{children}</table>
    </div>
  );
}
export function THead({ children }) {
  return <thead className="sticky top-0 z-[1] bg-[#f7f7f8]">{children}</thead>;
}
export function TH({ className, children, align, ...props }) {
  return (
    <th
      className={cn(
        "h-9 border-b border-line px-3.5 text-left text-[12px] font-semibold whitespace-nowrap text-muted",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}
export function TR({ className, children, onClick, ...props }) {
  return (
    <tr onClick={onClick} className={cn("border-b border-[#efeff3] last:border-0 hover:bg-black/[0.025]", onClick && "cursor-pointer", className)} {...props}>
      {children}
    </tr>
  );
}
export function TD({ className, children, align, ...props }) {
  return (
    <td className={cn("h-10 px-3.5 text-text", align === "right" && "text-right tabular-nums", align === "center" && "text-center", className)} {...props}>
      {children}
    </td>
  );
}
/** Full-width row for loading / empty states inside a table body. */
export function TableMessage({ colSpan, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-12 text-center text-[13px] text-muted">
        {children}
      </td>
    </tr>
  );
}

/* ---------- Misc ---------- */
const badgeTones = {
  neutral: "bg-black/[0.05] text-muted",
  accent: "bg-accent-soft text-accent",
  success: "bg-success/10 text-success",
  warning: "bg-warning/10 text-warning",
  danger: "bg-danger/10 text-danger",
  info: "bg-info/10 text-info",
};
export function Badge({ tone = "neutral", dot, className, children }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium whitespace-nowrap", badgeTones[tone], className)}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Spinner({ className, label = "Loading…" }) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 py-16 text-[12.5px] text-faint", className)}>
      <Loader2 className="size-6 animate-spin text-accent" />
      {label}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      {Icon && (
        <div className="grid size-10 place-items-center rounded-full bg-accent-soft text-accent">
          <Icon className="size-5" />
        </div>
      )}
      <h3 className="m-0 mt-3 text-[14px] font-semibold text-fg">{title}</h3>
      {description && <p className="m-0 mt-1 max-w-sm text-[13px] text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Small key/value block used on detail pages. */
export function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[11.5px] font-medium text-faint">{label}</div>
      <div className="mt-0.5 text-[13.5px] text-fg">{children ?? "—"}</div>
    </div>
  );
}

/** Filter row above a list: a responsive grid of fields with optional actions on the right. */
export function FilterBar({ children, actions, className }) {
  return (
    <Card className={cn("p-4", className)}>
      <div className="grid grid-cols-1 items-end gap-x-4 sm:grid-cols-2 lg:grid-cols-4 [&>*]:min-w-0">{children}</div>
      {actions && <div className="mt-1 flex flex-wrap justify-end gap-2">{actions}</div>}
    </Card>
  );
}

/** Small icon button for table rows (download, view, edit…). */
export function IconButton({ label, className, children, ...props }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn("inline-grid size-8 place-items-center rounded-lg text-muted hover:bg-black/[0.045] hover:text-fg disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4", className)}
      {...props}
    >
      {children}
    </button>
  );
}

/** Ascending/descending sort arrows for a column header (keeps the `pdf-hide` class used by PDF export). */
export function SortButtons({ onAsc, onDesc, className }) {
  const btn = "grid size-4 place-items-center rounded text-faint hover:bg-black/[0.06] hover:text-fg";
  return (
    <span className={cn("pdf-hide ml-1 inline-flex align-middle", className)}>
      <button type="button" aria-label="Sort ascending" className={btn} onClick={onAsc}>
        <ArrowUp className="size-3" />
      </button>
      <button type="button" aria-label="Sort descending" className={btn} onClick={onDesc}>
        <ArrowDown className="size-3" />
      </button>
    </span>
  );
}
